import React, { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { ChevronDown, Search } from 'lucide-react';
import { KeyType, RegisterKey } from '../../types/keyRegister';
import { handOutGroups } from './handOutKeys';
import { keyCountLabel, keySerialLabel } from './keyRegisterLabels';

interface HandOutKeyGroupProps {
    type: KeyType;
    /** The type's in-stock keys matching the search. */
    keys: RegisterKey[];
    open: boolean;
    /** While searching every group is open and the header does not toggle. */
    searching: boolean;
    picked: Set<string>;
    onToggleOpen: () => void;
    onToggleKey: (keyKey: string) => void;
}

/** One type: a header with chevron, name and "N inne" / "N av M valda", and its checkboxes when open. */
const HandOutKeyGroup: React.FC<HandOutKeyGroupProps> = ({ type, keys, open, searching, picked, onToggleOpen, onToggleKey }) => {
    const pickedCount = keys.filter(key => picked.has(key.key)).length;
    const bodyId = `hand-out-keys-${type.key}`;
    return (
        <div className={`hand-out-keys-group${open ? ' hand-out-keys-group--open' : ''}`}>
            <button
                type="button"
                className="hand-out-keys-type"
                aria-expanded={open}
                aria-controls={bodyId}
                onClick={onToggleOpen}
                disabled={searching}
            >
                <ChevronDown size={18} className="hand-out-keys-chevron" aria-hidden="true" />
                <span className="hand-out-keys-type-name">{type.name}</span>
                <span className="hand-out-keys-type-count">
                    {pickedCount > 0 ? `${pickedCount} av ${keys.length} valda` : `${keys.length} inne`}
                </span>
            </button>
            {open && (
                <div id={bodyId} role="group" aria-label={type.name} className="hand-out-keys-items">
                    {keys.map(key => (
                        <Form.Check
                            key={key.key}
                            type="checkbox"
                            id={`hand-out-key-${key.key}`}
                            className="hand-out-keys-item"
                            checked={picked.has(key.key)}
                            onChange={() => onToggleKey(key.key)}
                            label={(
                                <>
                                    {keySerialLabel(key)}
                                    {key.location && <span className="text-muted"> · {key.location}</span>}
                                </>
                            )}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

interface HandOutKeyListProps {
    types: KeyType[];
    keysByType: { [typeKey: string]: RegisterKey[] };
    selected: string[];
    onChange: (keyKeys: string[]) => void;
    error?: string;
}

/**
 * The keys of "Lämna ut nycklar": a search over the in-stock keys, grouped by type, one checkbox per
 * key. Types start collapsed, with their picked count on the header; while searching every match is
 * open. A key picked before a search stays picked while the search hides it.
 */
const HandOutKeyList: React.FC<HandOutKeyListProps> = ({ types, keysByType, selected, onChange, error }) => {
    const [query, setQuery] = useState('');
    const groups = useMemo(() => handOutGroups(types, keysByType, query), [types, keysByType, query]);
    const picked = useMemo(() => new Set(selected), [selected]);
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
    const searching = query.trim() !== '';

    const toggleType = (typeKey: string) => setExpanded(current => {
        const next = new Set(current);
        if (next.has(typeKey)) next.delete(typeKey);
        else next.add(typeKey);
        return next;
    });

    const toggle = (keyKey: string) =>
        onChange(picked.has(keyKey) ? selected.filter(k => k !== keyKey) : [...selected, keyKey]);

    return (
        <Form.Group className="mb-3">
            <div className="hand-out-keys-label">
                <Form.Label className="mb-0">Nycklar *</Form.Label>
                <span className="text-muted small" aria-live="polite">
                    {selected.length === 0 ? 'Inga valda' : `${keyCountLabel(selected.length)} valda`}
                </span>
            </div>
            <div className="hand-out-keys-search">
                <Search size={16} aria-hidden="true" />
                <Form.Control
                    type="search"
                    placeholder="Sök nyckeltyp, nummer eller förvaring"
                    aria-label="Sök nycklar"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
            </div>
            <div className={`hand-out-keys-list${error ? ' is-invalid' : ''}`}>
                {groups.length === 0 ? (
                    <p className="text-muted mb-0 p-2">Inga nycklar inne matchar sökningen</p>
                ) : groups.map(({ type, keys }) => (
                    <HandOutKeyGroup
                        key={type.key}
                        type={type}
                        keys={keys}
                        open={searching || expanded.has(type.key)}
                        searching={searching}
                        picked={picked}
                        onToggleOpen={() => toggleType(type.key)}
                        onToggleKey={toggle}
                    />
                ))}
            </div>
            {error && <div className="invalid-feedback d-block">{error}</div>}
        </Form.Group>
    );
};

export default HandOutKeyList;
